import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, Stack, SvgIcon, useMediaQuery, useTheme, Tab, Tabs, CircularProgress } from '@mui/material';
import { Plus } from '../../../assets';
import useLogError from '../../../hooks/useLogError';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { Section } from '../../../components/ui/Section';
import ExchangeRatesTable from './components/ExchangeRatesTable';
import CreateUpdateUserModal from './components/CreateUpdateRateModal';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { client, CurrencyConversionContent, ExchangeRateRequest, PaginatedList } from '../../../app/services/api/orderManagementClient';
import { UserContext } from '../../../components/shared/useUser';
import { ExchangeRateTab } from './enums/ExchangeRateTab';

const ManageExchangeRates = () => {
  const { t } = useTranslation('manageExchangeRates');
  const [paginatedRates, setPaginatedRates] = useState<PaginatedList<CurrencyConversionContent>>();
  const { selectedStore } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { logError } = useLogError();
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [showCreateSuccessMessage, setShowCreateSuccessMessage] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const [activeTab, setActiveTab] = useState<ExchangeRateTab>(ExchangeRateTab.ACTIVE);
  const [showDeleteSuccessMessage, setShowDeleteSuccessMessage] = useState(false);
  const [showUpdateSuccessMessage, setShowUpdateSuccessMessage] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    loadData();
  }, [activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: ExchangeRateTab) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const loadData = () => {
    setIsLoading(true);

    client.getExchangeRates(selectedStore?.storeNumber, page, 10, activeTab === ExchangeRateTab.ACTIVE)
      .then((x) => {
        if (x.success) {
          setPaginatedRates(x.data);
        }
      })
      .catch(logError)
      .finally(() => setIsLoading(false));
  };

  const handleCreate = async (body: ExchangeRateRequest) => {
    setIsLoading(true);
    try {
      const result = await client.updateExchangeRate(selectedStore?.storeNumber, body);
      if (result.success) {
        setShowCreateSuccessMessage(true);
        setShowCreateModal(false);
        loadData();
      } else {
        logError(result.message);
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setPage(0);
    loadData();
  };

  return (
    <PageArea>
      <Box
        display='flex'
        width='100%'
        height='100%'
        flexDirection='column'
      >
        <Grid container>
          <Stack
            width='100%'
            spacing={2}
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle={t('manageExchangeRates')}>{t('exchangeRates')}</PageTitle>
            </Box>
            <Box width={isMobile ? '100%' : 'auto'}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <Button
                variant="primary"
                size="lg"
                sx={{
                  mb: { xs: 4, md: 0 },
                }}
                onClick={() => setShowCreateModal(true)}
                startIcon={<SvgIcon><Plus fill="white" /></SvgIcon>}
              >
                {t('newRate')}
              </Button>
            </Box>
          </Stack>

          <Grid item
            xs={12}>
            <Tabs value={activeTab}
              onChange={handleTabChange}>
              <Tab label={t('activeTab')}
                value={ExchangeRateTab.ACTIVE} />
              <Tab label={t('historyTab')}
                value={ExchangeRateTab.HISTORY} />
            </Tabs>
          </Grid>

          <Grid item
            xs={12}
            sx={{ mt: '12px' }}>
            {isLoading ? (
              <Box display="flex" 
                justifyContent="center"
                alignItems="center" 
                minHeight="200px">
                <CircularProgress />
              </Box>
            ) : (
              paginatedRates &&
                <Section>
                  <ExchangeRatesTable
                    page={page}
                    setPage={setPage}
                    list={paginatedRates}
                    clearAll={handleClear}
                    loadData={loadData}
                    isLoading={isLoading}
                    onCreate={() => setShowCreateModal(true)}
                    activeTab={activeTab}
                    setShowUpdateSuccessMessage={setShowUpdateSuccessMessage}
                    setShowDeleteSuccessMessage={setShowDeleteSuccessMessage}
                  />
                </Section>
            )}
          </Grid>
        </Grid>
        <CreateUpdateUserModal
          openModal={showCreateModal}
          onConfirm={handleCreate}
          selectedRate={undefined}
          onCancel={() => setShowCreateModal(false)}
        />
        <MessagesSnackbar
          open={showCreateSuccessMessage}
          onClose={() => setShowCreateSuccessMessage(false)}
          message={t('successCreateMessage')}
          severity="success"
          duration={null}
        />
        <MessagesSnackbar
          open={showDeleteSuccessMessage}
          onClose={() => setShowDeleteSuccessMessage(false)}
          message={t('rateDeleted')}
          severity="success"
          duration={null}
        />
        <MessagesSnackbar
          open={showUpdateSuccessMessage}
          onClose={() => setShowUpdateSuccessMessage(false)}
          message={t('successUpdateMessage')}    
          severity="success"
          duration={null}
        />
      </Box>
    </PageArea>
  );
};

export default ManageExchangeRates;